// hooks
import React, { useState, useEffect, useRef, useMemo } from 'react';

// Material UI
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";


// Material UI icons (outlined variants)
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Edit, Delete } from '@mui/icons-material';

// My modules
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from 'auth/Auth';
import { useCurrency } from 'context/CurrencyContext';
import { useCountryCodes } from 'hooks/useCountryCodes';
import { CountrySelect, StateSelect, CitySelect } from 'components/GeographySelect';
import { fetchStatesByCountry, fetchCitiesByCountryAndState } from 'api/countriesNow';
import { v4 as uuidv4 } from 'uuid';
import StorageIndicator from 'components/SettingsComponents/components/StorageIndicator';
import {
  DEFAULT_PHONE_DIAL_CODE,
  parseStoredPhone,
  composePhone,
  isExactTenDigitPhone,
} from 'utils/companyPhone';
import CustomFieldsSettings from 'components/CustomFields/CustomFieldsSettings';
import TemplatesSettings from 'components/SettingsComponents/TemplatesSettings';
import GeographySettingsPanel from 'layouts/company-settings/GeographySettingsPanel';
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import {
    uploadPrivateObject,
    fetchS3Attachments,
    uploadReplaceImage,
    getImageAttachment,
    getImageURL,
    deleteImageObject
} from 'utils/S3';
import AttachmentUploadModal from "components/CustomModals/AttachmentUploadModal";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const isIndiaCountry = (country) => {
    const n = String(country || '').trim().toLowerCase();
    // Master name plus common tenant display renames still count as India for GST fields.
    return n === 'india' || n === 'bharat' || n === 'republic of india';
};

// Tab panel definitions.
// Add the "component" key later when each tab's content is ready.

const TAB_ITEMS = [
    { label: "Company Details", icon: <ApartmentOutlinedIcon fontSize="small" /> },
    { label: "Geography", icon: <PublicOutlinedIcon fontSize="small" /> },
    { label: "Custom Fields", icon: <SettingsOutlinedIcon fontSize="small" /> },
    { label: "Templates", icon: <DescriptionOutlinedIcon fontSize="small" /> },
    { label: "Tax Details", icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
];

function TaxesTable({ taxes, onEdit, onDelete, onCancel, editingTaxId, isLoading }) {
    const columnHeader = ["TAX TYPE", "RATE (%)", "STATUS", "ACTIONS"];
    const gridTemplateColumns = "2fr 1fr 1fr 120px";

    return (
        <MDBox sx={{ border: "1px solid #E8EDF5", borderRadius: "0.75rem", overflow: "auto" }}>
            <MDBox
                sx={{
                    display: "grid",
                    gridTemplateColumns,
                    bgcolor: "#F4F6F9",
                    borderBottom: "1px solid #E8EDF5",
                    px: 2,
                    py: 1.25,
                    minWidth: "max-content",
                    alignItems: "center",
                }}
            >
                {columnHeader.map((col) => (
                    <MDTypography key={col} variant="caption" fontWeight="bold" color="text" sx={{ letterSpacing: "0.03em" }}>
                        {col}
                    </MDTypography>
                ))}
            </MDBox>

            {
                isLoading
                    ? (
                        <MDBox
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "max-content",
                                width: "100%",
                                py: 4,
                            }}
                        >
                            <CircularProgress size={22} thickness={5} />
                        </MDBox>
                    )
                    : !taxes?.length
                        ? (
                            <MDBox
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "max-content",
                                    width: "100%",
                                    py: 4,
                                }}
                            >
                                <MDTypography variant="button" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                                    No tax configured yet
                                </MDTypography>
                            </MDBox>
                        )
                        : taxes.map((tax, index) => {
                        const isEditingRow = tax.id === editingTaxId;
                        return (
                            <MDBox
                                key={tax.id}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns,
                                    alignItems: "center",
                                    px: 2,
                                    py: 1.5,
                                    minWidth: "max-content",
                                    borderBottom: index === taxes.length - 1 ? "none" : "1px solid #E8EDF5",
                                    bgcolor: isEditingRow ? "#E3F2FD" : "transparent",
                                    transition: "background-color 0.15s ease",
                                    "&:hover": { bgcolor: isEditingRow ? "#E3F2FD" : "#FAFBFD" },
                                }}
                            >
                                <MDTypography variant="button" fontWeight="medium" color="dark">
                                    {tax.tax_type}
                                </MDTypography>

                                <MonoText variant="button" color="text">
                                    {tax.rate}%
                                </MonoText>

                                <MDBox sx={{ justifySelf: 'start' }}>
                                    <StatusBadge status={tax.is_active ? "active" : "inactive"} />
                                </MDBox>

                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                    {isEditingRow ? (
                                        <MDButton
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            startIcon={<CloseIcon fontSize="small" />}
                                            onClick={onCancel}
                                            sx={{ py: 0.25, px: 1 }}
                                        >
                                            Cancel
                                        </MDButton>
                                    ) : (
                                        <>
                                            <IconButton size="small" onClick={() => onEdit(tax)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => onDelete(tax.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            </MDBox>
                        );
                    })
            }
        </MDBox>
    );
}

function TaxesPagination({ page, totalPages, totalEntries, pageSize, onPageChange }) {
    if (totalEntries === 0) return null;

    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, totalEntries);

    return (
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mt={2}>
            <MDTypography variant="caption" color="text">
                Showing {start} to {end} of {totalEntries} entries
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1}>
                <IconButton size="small" disabled={page === 1} onClick={() => onPageChange(page - 1)}>
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <MDButton
                        key={p}
                        variant={p === page ? "contained" : "text"}
                        color={p === page ? "info" : "secondary"}
                        size="small"
                        onClick={() => onPageChange(p)}
                        sx={{ minWidth: 32, width: 32, height: 32, padding: 0, borderRadius: '8px' }}
                    >
                        {p}
                    </MDButton>
                ))}
                <IconButton size="small" disabled={page === totalPages} onClick={() => onPageChange(page + 1)}>
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </MDBox>
        </MDBox>
    );
}

function FieldLabel({ label, required, hint }) {
    return (
        <Box mb={0.5}>
            <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
                {label}
                {required && (
                    <Box component="span" sx={{ color: "#E53935", ml: 0.25 }}>
                        *
                    </Box>
                )}
            </MDTypography>
            {hint && (
                <MDTypography variant="caption" color="text" opacity={0.8}>
                    {hint}
                </MDTypography>
            )}
        </Box>
    );
}

const CompanySettings = () => {
  const isV2 = useTheme()?.pearlUi === "v2";

    // Initializing states

    const [activeTab, setActiveTab] = useState(0);
    const tabRefs = useRef([]);
    const [indicator, setIndicator] = useState({ left: 0, width: 0 });

    useEffect(() => {
        const currentTab = tabRefs.current[activeTab];
        if (currentTab) {
            setIndicator({
                left: currentTab.offsetLeft,
                width: currentTab.offsetWidth,
            });
        }
    }, [activeTab]);

    // Company Details tab state (ported from the old GeneralSettings.js flow)

    const { axiosInstance } = useAuth();
    const { updateCurrency, availableCurrencies } = useCurrency();
    const { countryCodes, getByDialCode } = useCountryCodes();

    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [phoneDialCode, setPhoneDialCode] = useState(DEFAULT_PHONE_DIAL_CODE);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [companyDetails, setCompanyDetails] = useState({
        company_name: "",
        address: "",
        city: "",
        state: "",
        stcd: "",
        zip_code: "",
        country: "",
        phone: "",
        email: "",
        website: "",
        tax_id: "",
        currency: "",
        bank_name: "",
        account_name: "",
        account_number: "",
        routing_number: "",
        swift_code: "",
        logo_url: null,
        logo_bucket: null,
        logo_storage_path: null,
        brand_primary_color: null,
        brand_secondary_color: null,
    });
    const [originalDetails, setOriginalDetails] = useState({});
    const [tempLogo, setTempLogo] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchCompanyDetails();
    }, []);

    const applyPhoneFromDetails = (data) => {
        const parsed = parseStoredPhone(data?.phone, countryCodes);
        setPhoneDialCode(parsed.dialCode);
        setPhoneNumber(parsed.number);
    };

    const fetchCompanyDetails = async () => {
        setIsLoading(true);
        try {
            const { data } = await axiosInstance.get('/company/info');
            setCompanyDetails(data);
            setOriginalDetails(data);
            applyPhoneFromDetails(data);
        } catch (error) {
            console.error('Error fetching company details:', error);
            setSnackbar({ open: true, message: 'Error loading company details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Attachment states

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);

    // Fetch existing attachment when the page loads up.

    useEffect(() => {
        const fetchAttachments = async () => {
            await fetchS3Attachments({
                axiosInstance: axiosInstance,
                parent_id: originalDetails.id,
                parent_table: 'company_info',
                updateAttachments: setAttachments,
            });
        };
        fetchAttachments();
    }, [originalDetails]);

    useEffect(() => {
        const getImage = async () => {
            const imageURL = await getImageURL(attachments, axiosInstance);
            setImagePreviewUrl(imageURL);
        };
        getImage();
    }, [attachments]);

    // Handle Image Upload

    const handleUploadSuccess = async (uploadData) => {
        const updatedImage = await uploadReplaceImage(
            {
                axiosInstance: axiosInstance,
                updateIsAttachmentModalOpen: setIsAttachmentModalOpen,
                attachments: attachments,
                updateAttachments: setAttachments,
                uploadData: uploadData,
                parent_table: "company_info",
                parent_id: originalDetails.id,
            }
        );
        setImagePreviewUrl(updatedImage);
        // Persist durable R2 refs so PDFs can re-fetch the private logo after
        // the temporary download_url expires.
        try {
            await axiosInstance.put('/company/info', {
                logo_url: uploadData.url || null,
                logo_bucket: uploadData.bucket || null,
                logo_storage_path: uploadData.path || null,
            });
            setCompanyDetails((prev) => ({
                ...prev,
                logo_url: uploadData.url || prev.logo_url,
                logo_bucket: uploadData.bucket || prev.logo_bucket,
                logo_storage_path: uploadData.path || prev.logo_storage_path,
            }));
        } catch (error) {
            console.error('Error saving company logo refs:', error);
            setSnackbar({
                open: true,
                message: 'Logo uploaded but failed to save for PDFs. Please save company details.',
                severity: 'warning',
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const digits = String(phoneNumber || '').replace(/\D/g, '');
        const errors = {};
        if (!phoneDialCode || !getByDialCode(phoneDialCode)) {
            errors.phone = 'Select a country code';
        } else if (!isExactTenDigitPhone(digits)) {
            errors.phone = 'Phone must be exactly 10 digits (excluding country code)';
        }
        if (!companyDetails.country) {
            errors.country = 'Select a country';
        }

        const country = String(companyDetails.country || '').trim();
        const state = String(companyDetails.state || '').trim();
        const city = String(companyDetails.city || '').trim();
        const geoNorm = (value) => String(value || '').trim().toLowerCase();
        // Only validate geography when the user actually changed it — legacy
        // free-typed values must not block phone/bank/logo saves.
        const geoChanged =
            geoNorm(country) !== geoNorm(originalDetails.country) ||
            geoNorm(state) !== geoNorm(originalDetails.state) ||
            geoNorm(city) !== geoNorm(originalDetails.city);

        if (geoChanged && country && !errors.country) {
            try {
                const states = await fetchStatesByCountry(axiosInstance, country);
                const stateOptions = (states || []).map((s) => String(s).trim()).filter(Boolean);
                if (state) {
                    const stateOk = stateOptions.some(
                        (s) => s.toLowerCase() === state.toLowerCase()
                    );
                    if (stateOptions.length > 0 && !stateOk) {
                        errors.state =
                            `Select a valid State/Province for ${country}. Manual values are not allowed.`;
                    }
                } else if (stateOptions.length > 0) {
                    errors.state = 'Select a State/Province for the chosen country';
                }

                if (!errors.state && (state || city)) {
                    const cities = await fetchCitiesByCountryAndState(
                        axiosInstance,
                        country,
                        state
                    );
                    const cityOptions = (cities || []).map((c) => String(c).trim()).filter(Boolean);
                    if (city) {
                        const cityOk = cityOptions.some(
                            (c) => c.toLowerCase() === city.toLowerCase()
                        );
                        if (cityOptions.length > 0 && !cityOk) {
                            errors.city =
                                `Select a valid City for ${state || country}. Manual values are not allowed.`;
                        }
                    }
                }
            } catch (geoErr) {
                console.error('Geography validation failed:', geoErr);
                errors.state = errors.state || 'Could not verify State/Province for this country';
            }
        }

        if (Object.keys(errors).length) {
            setFieldErrors(errors);
            setSnackbar({ open: true, message: Object.values(errors)[0], severity: 'error' });
            return;
        }
        setFieldErrors({});

        try {
            let updatedLogoUrl = companyDetails.logo_url;

            let updatedLogoBucket = companyDetails.logo_bucket || null;
            let updatedLogoPath = companyDetails.logo_storage_path || null;
            if (tempLogo) {
                const uploadResult = await uploadPrivateObject({
                    axiosInstance,
                    file: tempLogo.file,
                    env: process.env.REACT_APP_STORAGE_ENV,
                    featurePath: 'company-logos',
                    scope: 'org',
                });
                updatedLogoUrl = uploadResult.url;
                updatedLogoBucket = uploadResult.bucket;
                updatedLogoPath = uploadResult.path;
            }

            const phone = composePhone(phoneDialCode, digits);
            // GST State Code (stcd) is required for India e-invoicing; omit for other countries.
            const stcdRaw = isIndiaCountry(companyDetails.country)
                ? String(companyDetails.stcd ?? '').trim()
                : '';
            const stcdValue = stcdRaw === '' || Number.isNaN(Number(stcdRaw))
                ? null
                : Number(stcdRaw);

            // Omit logo storage refs unless they changed (new upload / clear).
            // Re-sending a legacy pair outside the tenant prefix 400s unrelated edits.
            const logoStorageChanged =
                (updatedLogoBucket || null) !== (originalDetails.logo_bucket || null) ||
                (updatedLogoPath || null) !== (originalDetails.logo_storage_path || null);
            const payload = {
                ...companyDetails,
                country,
                state,
                city,
                phone,
                logo_url: updatedLogoUrl,
                stcd: stcdValue,
            };
            if (logoStorageChanged) {
                payload.logo_bucket = updatedLogoBucket;
                payload.logo_storage_path = updatedLogoPath;
            } else {
                delete payload.logo_bucket;
                delete payload.logo_storage_path;
            }
            await axiosInstance.put('/company/info', payload);

            setSnackbar({ open: true, message: 'Company details updated successfully!', severity: 'success' });
            setIsEditing(false);
            setTempLogo(null);
            await fetchCompanyDetails();
        } catch (error) {
            console.error('Error updating company details:', error);
            const detail = error?.response?.data?.detail;
            setSnackbar({
                open: true,
                message:
                    typeof detail === 'string'
                        ? detail
                        : `Failed to update company details: ${error.message}`,
                severity: 'error',
            });
        }
    };

    const handleCompanyDetailChange = (e) => {
        const { name, value } = e.target;
        if (name === 'currency') {
            updateCurrency(value);
        }
        setCompanyDetails((prev) => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleCancel = () => {
        setCompanyDetails(originalDetails);
        applyPhoneFromDetails(originalDetails);
        setTempLogo(null);
        setFieldErrors({});
        setIsEditing(false);
    };

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const LoadingSkeleton = () => (
        <MDBox>
            {[...Array(6)].map((_, index) => (
                <MDBox
                    key={index}
                    sx={{
                        height: "48px",
                        backgroundColor: "rgba(200, 200, 200, 0.1)",
                        borderRadius: "8px",
                        mb: 2,
                        animation: "pulse 1.5s infinite ease-in-out",
                        "@keyframes pulse": {
                            "0%": { opacity: 1 },
                            "50%": { opacity: 0.5 },
                            "100%": { opacity: 1 },
                        },
                    }}
                />
            ))}
        </MDBox>
    );

    const renderTextField = (label, name, multiline = false) => {
        if (name === 'currency') {
            if (isEditing) {
                return (
                    <TextField
                        select
                        fullWidth
                        label={label}
                        name={name}
                        value={companyDetails[name] || ''}
                        onChange={handleCompanyDetailChange}
                        margin="dense"
                        size="small"
                        variant="outlined"
                        sx={{ mb: 1.5 }}
                    >
                        {availableCurrencies.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            }

            const currencyObj = availableCurrencies.find((c) => c.value === companyDetails[name]);
            const displayValue = currencyObj ? currencyObj.label : companyDetails[name];

            return (
                <MDBox mb={1.5}>
                    <MDTypography className="pearl-field-label" variant="caption" fontWeight="bold" color="text" sx={{ display: 'block', mb: 0.5 }}>
                        {label}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                        {displayValue || <span style={{ fontStyle: 'italic' }}>--</span>}
                    </MDTypography>
                </MDBox>
            );
        }

        if (isEditing) {
            return (
                <TextField
                    fullWidth
                    label={label}
                    name={name}
                    value={companyDetails[name] || ''}
                    onChange={handleCompanyDetailChange}
                    margin="dense"
                    size="small"
                    multiline={multiline}
                    rows={multiline ? 2 : 1}
                    variant="outlined"
                    inputProps={{ maxLength: name === 'stcd' ? 2 : undefined }}
                    sx={{ mb: 1.5 }}
                />
            );
        }

        return (
            <MDBox mb={1.5}>
                <MDTypography className="pearl-field-label" variant="caption" fontWeight="bold" color="text" sx={{ display: 'block', mb: 0.5 }}>
                    {label}
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ wordBreak: 'break-word' }}>
                    {companyDetails[name] || <span style={{ fontStyle: 'italic' }}>--</span>}
                </MDTypography>
            </MDBox>
        );
    };

    const renderPhoneField = () => {
        if (!isEditing) {
            return renderTextField('Phone', 'phone');
        }

        const selectedDial = getByDialCode(phoneDialCode) || { dialCode: phoneDialCode, country: phoneDialCode };

        return (
            <MDBox mb={1.5}>
                <MDTypography className="pearl-field-label" variant="caption" fontWeight="bold" color="text" sx={{ display: 'block', mb: 0.5 }}>
                    Phone
                </MDTypography>
                <MDBox display="flex" gap={1} alignItems="flex-start">
                    <Autocomplete
                        options={countryCodes}
                        value={selectedDial}
                        onChange={(_, value) => {
                            setPhoneDialCode(value?.dialCode || DEFAULT_PHONE_DIAL_CODE);
                            if (fieldErrors.phone) setFieldErrors((prev) => ({ ...prev, phone: '' }));
                        }}
                        getOptionLabel={(option) => option?.dialCode || ''}
                        isOptionEqualToValue={(option, value) => option?.dialCode === value?.dialCode}
                        renderOption={(props, option) => (
                            <MDBox component="li" {...props} key={`${option.iso2}-${option.dialCode}`} display="flex" gap={1}>
                                <MDTypography variant="caption">{option.dialCode}</MDTypography>
                                <MDTypography variant="caption" color="text">{option.country}</MDTypography>
                            </MDBox>
                        )}
                        slotProps={{ popper: { sx: { width: '280px !important' } } }}
                        sx={{ flex: '0 0 110px', minWidth: 110 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="+91"
                                error={Boolean(fieldErrors.phone)}
                            />
                        )}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="10-digit number"
                        value={phoneNumber}
                        error={Boolean(fieldErrors.phone)}
                        helperText={fieldErrors.phone || 'Exactly 10 digits (country code separate)'}
                        inputProps={{
                            inputMode: 'numeric',
                            maxLength: 10,
                            pattern: '[0-9]*',
                        }}
                        onChange={(e) => {
                            const next = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setPhoneNumber(next);
                            if (fieldErrors.phone) setFieldErrors((prev) => ({ ...prev, phone: '' }));
                        }}
                    />
                </MDBox>
            </MDBox>
        );
    };

    const renderRegisteredAddressFields = () => {
        const showStateCode = isIndiaCountry(companyDetails.country);

        if (!isEditing) {
            return (
                <>
                    {renderTextField('Street Address', 'address', true)}
                    {renderTextField('Country', 'country')}
                    {renderTextField('State / Province', 'state')}
                    {renderTextField('City', 'city')}
                    {showStateCode && renderTextField('GST State Code', 'stcd')}
                    {renderTextField('Postal / Zip Code', 'zip_code')}
                </>
            );
        }

        const selectSx = { mb: 1.5 };

        return (
            <>
                <TextField
                    fullWidth
                    label="Street Address"
                    name="address"
                    value={companyDetails.address || ''}
                    onChange={handleCompanyDetailChange}
                    margin="dense"
                    size="small"
                    multiline
                    rows={2}
                    variant="outlined"
                    placeholder="Building, street, area"
                    sx={selectSx}
                />
                <MDBox sx={selectSx}>
                    <CountrySelect
                        label="Country"
                        value={companyDetails.country || ''}
                        size="small"
                        inputHeight="40px"
                        required
                        error={Boolean(fieldErrors.country)}
                        helperText={fieldErrors.country || ''}
                        onChange={(nextCountry) => {
                            const india = isIndiaCountry(nextCountry);
                            setCompanyDetails((prev) => ({
                                ...prev,
                                country: nextCountry || '',
                                state: '',
                                city: '',
                                // Clear stcd when leaving India; keep/edit when switching to India.
                                ...(!india ? { stcd: '' } : {}),
                            }));
                            if (fieldErrors.country) {
                                setFieldErrors((prev) => ({ ...prev, country: '' }));
                            }
                        }}
                    />
                </MDBox>
                <MDBox sx={selectSx}>
                    <StateSelect
                        label="State / Province"
                        country={companyDetails.country || ''}
                        value={companyDetails.state || ''}
                        size="small"
                        inputHeight="40px"
                        freeSolo={false}
                        required
                        error={Boolean(fieldErrors.state)}
                        helperText={fieldErrors.state || 'Choose from the list for the selected country'}
                        onChange={(nextState) => {
                            setCompanyDetails((prev) => ({
                                ...prev,
                                state: nextState || '',
                                city: '',
                            }));
                            if (fieldErrors.state || fieldErrors.city) {
                                setFieldErrors((prev) => ({ ...prev, state: '', city: '' }));
                            }
                        }}
                    />
                </MDBox>
                <MDBox sx={selectSx}>
                    <CitySelect
                        label="City"
                        country={companyDetails.country || ''}
                        state={companyDetails.state || ''}
                        value={companyDetails.city || ''}
                        size="small"
                        inputHeight="40px"
                        freeSolo={false}
                        error={Boolean(fieldErrors.city)}
                        helperText={fieldErrors.city || 'Choose from the list for the selected state'}
                        onChange={(nextCity) => {
                            setCompanyDetails((prev) => ({
                                ...prev,
                                city: nextCity || '',
                            }));
                            if (fieldErrors.city) {
                                setFieldErrors((prev) => ({ ...prev, city: '' }));
                            }
                        }}
                    />
                </MDBox>
                {showStateCode && (
                    <TextField
                        fullWidth
                        label="GST State Code"
                        name="stcd"
                        value={companyDetails.stcd || ''}
                        onChange={handleCompanyDetailChange}
                        margin="dense"
                        size="small"
                        variant="outlined"
                        required
                        helperText="2-digit GST state code (required for India e-invoicing)"
                        inputProps={{ maxLength: 2, inputMode: 'numeric' }}
                        sx={selectSx}
                    />
                )}
                {renderTextField('Postal / Zip Code', 'zip_code')}
            </>
        );
    };

    // Tax Details tab state

    const [taxes, setTaxes] = useState([]);
    const [isTaxesLoading, setIsTaxesLoading] = useState(false);
    const [isSavingTax, setIsSavingTax] = useState(false);
    const [editingTaxId, setEditingTaxId] = useState(null);
    const [taxForm, setTaxForm] = useState({ tax_type: '', rate: '' });
    const [isTaxActive, setIsTaxActive] = useState(true);    const [taxFieldErrors, setTaxFieldErrors] = useState({});
    const [deleteTaxConfirm, setDeleteTaxConfirm] = useState({ open: false, id: null });

    useEffect(() => {
        setTaxFieldErrors({});
    }, [activeTab]);

    const [taxSearchQuery, setTaxSearchQuery] = useState('');
    const [taxPage, setTaxPage] = useState(1);
    const TAX_ROWS_PER_PAGE = 5;

    const filteredTaxes = useMemo(() => {
        const query = taxSearchQuery.trim().toLowerCase();
        if (!query) return taxes;
        return taxes.filter((tax) =>
            String(tax.tax_type).toLowerCase().includes(query) ||
            String(tax.rate).toLowerCase().includes(query) ||
            (tax.is_active ? 'active' : 'inactive').includes(query)
        );
    }, [taxes, taxSearchQuery]);

    const taxTotalPages = Math.max(1, Math.ceil(filteredTaxes.length / TAX_ROWS_PER_PAGE));

    useEffect(() => {
        setTaxPage(1);
    }, [taxSearchQuery]);

    useEffect(() => {
        if (taxPage > taxTotalPages) setTaxPage(taxTotalPages);
    }, [taxTotalPages, taxPage]);

    const paginatedTaxes = useMemo(() => {
        const start = (taxPage - 1) * TAX_ROWS_PER_PAGE;
        return filteredTaxes.slice(start, start + TAX_ROWS_PER_PAGE);
    }, [filteredTaxes, taxPage]);

    useEffect(() => {
        fetchTaxes();
    }, []);

    const fetchTaxes = async () => {
        setIsTaxesLoading(true);
        try {
            const response = await axiosInstance.get('/taxes');
            setTaxes(response.data);
        } catch (error) {
            console.error('Error fetching taxes:', error);
            setSnackbar({ open: true, message: 'Error loading taxes', severity: 'error' });
        } finally {
            setIsTaxesLoading(false);
        }
    };

    const handleTaxFormChange = (e) => {
        const { name, value } = e.target;
        setTaxForm((prev) => ({ ...prev, [name]: value }));
        if (taxFieldErrors[name]) {
            setTaxFieldErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateTaxForm = () => {
        const errors = {};
        const trimmedType = taxForm.tax_type.trim();

        if (!trimmedType) {
            errors.tax_type = 'Tax Type is required';
        } else if (trimmedType.length > 255) {
            errors.tax_type = 'Tax Type cannot exceed 255 characters';
        }

        if (taxForm.rate === '' || taxForm.rate === null) {
            errors.rate = 'Rate is required';
        } else {
            const rateNum = Number(taxForm.rate);
            if (Number.isNaN(rateNum)) {
                errors.rate = 'Rate must be a number';
            } else if (rateNum < 0 || rateNum > 100) {
                errors.rate = 'Rate must be between 0 and 100';
            }
        }

        setTaxFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleTaxStatusToggle = (e) => {
        setIsTaxActive(e.target.checked);
    };

    const handleResetTaxForm = () => {
        setEditingTaxId(null);
        setTaxForm({ tax_type: '', rate: '' });
        setIsTaxActive(true);
        setTaxFieldErrors({});
    };

    const handleEditTaxClick = (tax) => {
        setEditingTaxId(tax.id);
        setTaxForm({ tax_type: tax.tax_type, rate: tax.rate });
        setIsTaxActive(tax.is_active);
        setTaxFieldErrors({});
    };

    const handleSaveTax = async () => {
        if (!validateTaxForm()) {
            return;
        }
        setIsSavingTax(true);
        try {
            const payload = {
                tax_type: taxForm.tax_type.trim(),
                rate: taxForm.rate,
                is_active: isTaxActive,
            };

            if (editingTaxId) {
                await axiosInstance.put(`/taxes/${editingTaxId}`, payload);
                setSnackbar({ open: true, message: 'Tax updated successfully', severity: 'success' });
            } else {
                await axiosInstance.post('/taxes', payload);
                setSnackbar({ open: true, message: 'Tax added successfully', severity: 'success' });
            }

            handleResetTaxForm();
            fetchTaxes();
        } catch (error) {
            console.error('Error saving tax:', error);
            setSnackbar({ open: true, message: 'Error saving tax', severity: 'error' });
        } finally {
            setIsSavingTax(false);
        }
    };

    const handleDeleteTaxClick = (id) => setDeleteTaxConfirm({ open: true, id });
    const handleCloseDeleteTaxModal = () => setDeleteTaxConfirm({ open: false, id: null });

    const handleDeleteTax = async () => {
        try {
            await axiosInstance.delete(`/taxes/${deleteTaxConfirm.id}`);
            setSnackbar({ open: true, message: 'Tax deleted successfully', severity: 'success' });
            if (editingTaxId === deleteTaxConfirm.id) handleResetTaxForm();
            fetchTaxes();
        } catch (error) {
            console.error('Error deleting tax:', error);
            setSnackbar({ open: true, message: 'Error deleting tax', severity: 'error' });
            throw error;
        }
    };

    const handleDeleteCompanyImage = async () => {
        const linkedAttachment = getImageAttachment(attachments);
        await deleteImageObject(axiosInstance, linkedAttachment, setAttachments);
        setImagePreviewUrl(null);
        try {
            await axiosInstance.put('/company/info', {
                logo_url: null,
                logo_bucket: null,
                logo_storage_path: null,
            });
            setCompanyDetails((prev) => ({
                ...prev,
                logo_url: null,
                logo_bucket: null,
                logo_storage_path: null,
            }));
        } catch (error) {
            console.error('Error clearing company logo refs:', error);
        }
    };

    // Component

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={3} pb={3}>
                <Grid container spacing={3}>

                    {/* Page Header */}

                    <Grid item xs={12}>
                        {isV2 ? (
                          <PageHeader title="Company Settings" summary="Manage your company information, preferences and global configurations" />
                        ) : (
                        <MDBox display="flex" alignItems="center" gap={2}>
                            <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "12px",
                                    backgroundColor: ({ palette }) => "#d7e3fc",
                                    overflow: "hidden",
                                }}
                            >
                                <ApartmentOutlinedIcon sx={{ color: "info.main" }} />
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="h4" fontWeight="bold">
                                    Company Settings
                                </MDTypography>
                                <MDTypography variant="button" color="text" fontWeight="regular">
                                    Manage your company information, preferences and global configurations
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                        )}
                    </Grid>

                    {/* Tabs */}

                    <Grid item xs={12}>
                        <MDBox
                            bgColor="white"
                            position="relative"
                            display="flex"
                            sx={{
                                border: "1px solid",
                                borderRadius: "5px",
                                borderColor: "divider",
                                overflow: "hidden",
                            }}
                        >
                            {TAB_ITEMS.map((tab, index) => {
                                const isSelected = activeTab === index;
                                return (
                                    <MDBox
                                        key={tab.label}
                                        ref={(el) => (tabRefs.current[index] = el)}
                                        onClick={() => setActiveTab(index)}
                                        sx={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                            py: 2,
                                            cursor: "pointer",
                                            userSelect: "none",
                                            color: isSelected ? "info.main" : "text.secondary",
                                            transition: "color 0.3s ease",
                                            "& svg": { fontSize: "1.125rem" },
                                        }}
                                    >
                                        {tab.icon}
                                        <MDTypography
                                            variant="button"
                                            fontWeight={isSelected ? "bold" : "regular"}
                                            sx={{ color: "inherit", transition: "color 0.3s ease" }}
                                        >
                                            {tab.label}
                                        </MDTypography>
                                    </MDBox>
                                );
                            })}

                            {/* Sliding indicator */}
                            <MDBox
                                sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    height: "2px",
                                    backgroundColor: "info.main",
                                    left: `${indicator.left}px`,
                                    width: `${indicator.width}px`,
                                    transition: "left 0.3s ease, width 0.3s ease",
                                }}
                            />
                        </MDBox>
                    </Grid>

                    {/* Tab Content */}

                    <Grid item xs={12}>
                        <MDBox>
                            {activeTab === 0 && (
                                <MDBox>
                                    <MDBox
                                        bgColor="white"
                                        sx={{
                                            backgroundColor: 'white',
                                            borderRadius: '12px',
                                            p: 3,
                                            mb: 3,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                        }}
                                    >
                                        <Grid container spacing={3} alignItems="center">
                                            <Grid item>
                                                <MDBox
                                                    onClick={() => {
                                                        if (!imagePreviewUrl) setIsAttachmentModalOpen(true);
                                                    }}
                                                    sx={{
                                                        width: '100px',
                                                        height: '100px',
                                                        borderRadius: '12px',
                                                        backgroundColor: '#f8fafc',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        overflow: 'hidden',
                                                        border: '1px solid #eee',
                                                        position: 'relative',
                                                        cursor: imagePreviewUrl ? 'default' : 'pointer',
                                                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                                                        ...(!imagePreviewUrl && {
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                                                borderColor: '#6366F1',
                                                            },
                                                            '&:hover .upload-icon, &:hover .upload-text': {
                                                                color: '#6366F1',
                                                            },
                                                        }),
                                                        '&:hover .logo-overlay': {
                                                            opacity: imagePreviewUrl ? 1 : 0,
                                                        },
                                                    }}
                                                >
                                                    {imagePreviewUrl ? (
                                                        <img
                                                            src={imagePreviewUrl}
                                                            alt="Company Logo"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    ) : (
                                                        <MDBox
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 0.5,
                                                                px: 1,
                                                            }}
                                                        >
                                                            <ImageOutlinedIcon
                                                                className="upload-icon"
                                                                sx={{
                                                                    fontSize: '40px !important',
                                                                    color: '#94A3B8',
                                                                    transition: 'color 0.2s ease',
                                                                }}
                                                            />
                                                            <MDTypography
                                                                className="upload-text"
                                                                sx={{
                                                                    fontSize: '10px',
                                                                    lineHeight: 1.2,
                                                                    color: '#94A3B8',
                                                                    textAlign: 'center',
                                                                    fontWeight: 500,
                                                                    transition: 'color 0.2s ease',
                                                                }}
                                                            >
                                                                Upload Logo
                                                            </MDTypography>
                                                        </MDBox>
                                                    )}

                                                    <AttachmentUploadModal
                                                        isOpen={isAttachmentModalOpen}
                                                        onClose={(e) => {
                                                            e?.stopPropagation();
                                                            setIsAttachmentModalOpen(false);
                                                        }}
                                                        onUpload={handleUploadSuccess}
                                                        axiosInstance={axiosInstance}
                                                        folderPath="company-logos"
                                                        scope="org"
                                                        allowedFileTypes="image/*"
                                                        maxSizeInMB={5}
                                                    />

                                                    {imagePreviewUrl && (
                                                        <MDBox
                                                            className="logo-overlay"
                                                            sx={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                backgroundColor: 'rgba(0,0,0,0.55)',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s ease',
                                                            }}
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setIsAttachmentModalOpen(true);
                                                                }}
                                                                sx={{
                                                                    backgroundColor: '#fff',
                                                                    '&:hover': { backgroundColor: '#f0f0f0' },
                                                                }}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>

                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setIsDeleteConfirmOpen(true);
                                                                }}
                                                                sx={{
                                                                    backgroundColor: '#fff',
                                                                    '&:hover': { backgroundColor: '#f0f0f0' },
                                                                }}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </MDBox>
                                                    )}
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs>
                                                <StorageIndicator />
                                            </Grid>

                                            {!isLoading && (
                                                <Grid item>
                                                    {isEditing ? (
                                                        <MDBox sx={{ display: 'flex', gap: 1 }}>
                                                            <MDButton
                                                                variant="contained"
                                                                onClick={handleCancel}
                                                                sx={{ minWidth: '40px', width: '40px', height: '40px', borderRadius: '50%', padding: 0 }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </MDButton>
                                                            <MDButton
                                                                type="submit"
                                                                variant="contained"
                                                                color="info"
                                                                onClick={handleSubmit}
                                                                sx={{ minWidth: '40px', width: '40px', height: '40px', borderRadius: '50%', padding: 0 }}
                                                            >
                                                                <SaveIcon fontSize="small" />
                                                            </MDButton>
                                                        </MDBox>
                                                    ) : (
                                                        <MDButton
                                                            variant="contained"
                                                            color="info"
                                                            onClick={() => setIsEditing(true)}
                                                            sx={{ minWidth: '40px', width: '40px', height: '40px', borderRadius: '50%', padding: 0 }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </MDButton>
                                                    )}
                                                </Grid>
                                            )}
                                        </Grid>
                                    </MDBox>

                                    {isLoading ? (
                                        <LoadingSkeleton />
                                    ) : (
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} lg={8}>
                                                <MDBox
                                                    bgColor="white"
                                                    sx={{
                                                        backgroundColor: 'white',
                                                        borderRadius: '12px',
                                                        p: 3,
                                                        height: '100%',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    }}
                                                >
                                                    <MDTypography variant="h6" fontWeight="medium" mb={3}>
                                                        Company Information
                                                    </MDTypography>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={6}>
                                                            {renderTextField('Company Name', 'company_name')}
                                                            {renderTextField('Email', 'email')}
                                                            {renderPhoneField()}
                                                            {renderTextField('Website', 'website')}
                                                            {renderTextField('Tax ID', 'tax_id')}
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <MDTypography variant="button" fontWeight="medium" color="text" sx={{ display: 'block', mb: 1.5 }}>
                                                                Registered Address
                                                            </MDTypography>
                                                            {renderRegisteredAddressFields()}
                                                        </Grid>
                                                    </Grid>
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} lg={4}>
                                                <MDBox
                                                    bgColor="white"
                                                    sx={{
                                                        backgroundColor: 'white',
                                                        borderRadius: '12px',
                                                        p: 3,
                                                        height: '100%',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    }}
                                                >
                                                    <MDTypography variant="h6" fontWeight="medium" mb={3}>
                                                        Banking Information
                                                    </MDTypography>
                                                    {renderTextField('Bank Name', 'bank_name')}
                                                    {renderTextField('Account Name', 'account_name')}
                                                    {renderTextField('Account Number', 'account_number')}
                                                    {renderTextField('Routing Number', 'routing_number')}
                                                    {renderTextField('SWIFT Code', 'swift_code')}
                                                    {renderTextField('Currency', 'currency')}
                                                </MDBox>
                                            </Grid>
                                        </Grid>
                                    )}
                                </MDBox>
                            )}
                            {activeTab === 1 && (
                                <GeographySettingsPanel />
                            )}
                            {activeTab === 2 && (
                                <MDBox bgColor="white" borderRadius="lg" shadow="sm" p={3}>
                                    <CustomFieldsSettings />
                                </MDBox>
                            )}
                            {activeTab === 3 && (
                                <TemplatesSettings />
                            )}

                            {/* Tax Details */}

                            {activeTab === 4 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} lg={8}>
                                        <MDBox bgColor="white" p={3} sx={{
                                            border: "1px solid",
                                            borderRadius: "5px",
                                            borderColor: "divider",
                                        }}>
                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                                                <MDBox>
                                                    <MDTypography variant="h6" fontWeight="medium">
                                                        Tax Details
                                                    </MDTypography>
                                                    <MDTypography variant="caption" sx={{color: "text.secondary"}} fontWeight="medium">
                                                        Manage tax configuration
                                                    </MDTypography>
                                                </MDBox>
                                                <TextField
                                                    size="small"
                                                    placeholder="Search tax..."
                                                    value={taxSearchQuery}
                                                    onChange={(e) => setTaxSearchQuery(e.target.value)}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{ width: 240 }}
                                                />
                                            </MDBox>
                                            <TaxesTable
                                                taxes={paginatedTaxes}
                                                onEdit={handleEditTaxClick}
                                                onDelete={handleDeleteTaxClick}
                                                onCancel={handleResetTaxForm}
                                                editingTaxId={editingTaxId}
                                                isLoading={isTaxesLoading}
                                            />
                                            <TaxesPagination
                                                page={taxPage}
                                                totalPages={taxTotalPages}
                                                totalEntries={filteredTaxes.length}
                                                pageSize={TAX_ROWS_PER_PAGE}
                                                onPageChange={setTaxPage}
                                            />
                                        </MDBox>
                                    </Grid>

                                    <Grid item xs={12} lg={4}>
                                        <MDBox bgColor="white" borderRadius="lg" shadow="sm" p={3}
                                               sx={{
                                                   border: "1px solid",
                                                   borderRadius: "5px",
                                                   borderColor: "divider",
                                               }}
                                        >
                                            <MDTypography variant="h6" fontWeight="medium" mb={3}>
                                                {editingTaxId ? `Edit Tax - ${taxForm.tax_type}` : "Add New Tax"}
                                            </MDTypography>

                                            <MDBox flex="1 1 220px" minWidth={200}>
                                                <FieldLabel label="Tax Type" required />
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="text"
                                                    name="tax_type"
                                                    placeholder="e.g. GST"
                                                    value={taxForm.tax_type}
                                                    error={Boolean(taxFieldErrors.tax_type)}
                                                    onChange={handleTaxFormChange}
                                                />
                                                <MDTypography
                                                    variant="caption"
                                                    color="error"
                                                    display="block"
                                                    mt={0.5}
                                                    sx={{ minHeight: '1em', visibility: taxFieldErrors.tax_type ? 'visible' : 'hidden' }}
                                                >
                                                    {taxFieldErrors.tax_type || 'error'}
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox flex="1 1 220px" minWidth={200}>
                                                <FieldLabel label="Rate (%)" required />
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    name="rate"
                                                    placeholder="e.g. 4.7"
                                                    value={taxForm.rate}
                                                    error={Boolean(taxFieldErrors.rate)}
                                                    onChange={handleTaxFormChange}
                                                />
                                                <MDTypography
                                                    variant="caption"
                                                    color="error"
                                                    display="block"
                                                    mt={0.5}
                                                    sx={{ minHeight: '1em', visibility: taxFieldErrors.rate ? 'visible' : 'hidden' }}
                                                >
                                                    {taxFieldErrors.rate || 'error'}
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox display="flex" alignItems="center" gap={1} mb={3}>
                                                <MDBox
                                                    onClick={() => setIsTaxActive((prev) => !prev)}
                                                    sx={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: '4px',
                                                        border: '2px solid',
                                                        borderColor: isTaxActive ? 'info.main' : '#B0BAC9',
                                                        backgroundColor: isTaxActive ? 'info.main' : 'transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s ease',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {isTaxActive && (
                                                        <MDBox
                                                            component="span"
                                                            sx={{
                                                                width: 9,
                                                                height: 5,
                                                                borderLeft: '2px solid white',
                                                                borderBottom: '2px solid white',
                                                                transform: 'rotate(-45deg) translate(1px, -1px)',
                                                            }}
                                                        />
                                                    )}
                                                </MDBox>
                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="regular"
                                                    onClick={() => setIsTaxActive((prev) => !prev)}
                                                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    Active
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox display="flex" gap={1}>
                                                <MDButton variant="outlined" color="dark" fullWidth onClick={handleResetTaxForm}>
                                                    Reset
                                                </MDButton>
                                                <MDButton
                                                    variant="contained"
                                                    color="info"
                                                    fullWidth
                                                    onClick={handleSaveTax}
                                                    disabled={isSavingTax}
                                                >
                                                    {editingTaxId ? "Update Tax" : "Add Tax"}
                                                </MDButton>
                                            </MDBox>
                                        </MDBox>
                                    </Grid>
                                </Grid>
                            )}
                        </MDBox>
                    </Grid>

                </Grid>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <ConfirmDeleteModal
                    open={deleteTaxConfirm.open}
                    onClose={handleCloseDeleteTaxModal}
                    onConfirm={handleDeleteTax}
                    title="Delete Tax"
                    message="Are you sure you want to delete this tax? This action cannot be undone."
                />

                <ConfirmDeleteModal
                    open={isDeleteConfirmOpen}
                    onClose={() => setIsDeleteConfirmOpen(false)}
                    onConfirm={handleDeleteCompanyImage}
                    title="Delete Company Image"
                    message="Are you sure you want to delete this image? This action cannot be undone."
                />

            </MDBox>
        </DashboardLayout>
    )

}

export default CompanySettings;